import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, DataTable } from '@/components/ui';
import type { DataTableColumn } from '@/components/ui';
import { formatDollars } from '../../../utils/formatDollars';
import {
  PO_TYPE_LABELS,
  type PoStatus,
} from '../../../services/purchaseOrderService';
import {
  projectService,
  type ProjectPurchaseOrder,
  type ProjectPurchaseOrderLine,
} from '../../../services/projectService';

const StatusBadge: React.FC<{ status: PoStatus }> = ({ status }) => {
  if (status === 'ACTIVE') return <Badge bg="success">Active</Badge>;
  if (status === 'INACTIVE') return <Badge bg="secondary">Inactive</Badge>;
  return <Badge bg="warning">Reserved</Badge>;
};

const lineAmount = (amount: number | null) => (amount == null ? '' : formatDollars(amount));

interface ProjectPurchaseOrdersTabProps {
  projectId: number;
}

const ProjectPurchaseOrdersTab: React.FC<ProjectPurchaseOrdersTabProps> = ({ projectId }) => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<ProjectPurchaseOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const rows = await projectService.getProjectPurchaseOrders(projectId);
        if (!cancelled) setOrders(rows || []);
      } catch (err) {
        if (!cancelled) {
          setOrders([]);
          setError(err instanceof Error ? err.message : 'Failed to load purchase orders');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (projectId) void load();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const toggleExpanded = useCallback((id: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const columns: DataTableColumn<ProjectPurchaseOrder>[] = [
    {
      key: 'expander',
      header: '',
      className: 'tw-w-8',
      cell: (po) => (
        <button
          type="button"
          aria-expanded={expanded.has(po.id)}
          aria-label={expanded.has(po.id) ? `Hide line items for ${po.poNumber}` : `Show line items for ${po.poNumber}`}
          className="tw-border-0 tw-bg-transparent tw-p-1 tw-text-content-muted hover:tw-text-content"
          onClick={(e) => {
            e.stopPropagation();
            toggleExpanded(po.id);
          }}
        >
          <i className={`bi ${expanded.has(po.id) ? 'bi-chevron-down' : 'bi-chevron-right'}`} />
        </button>
      ),
    },
    {
      key: 'poNumber',
      header: 'PO Number',
      mobile: 'title',
      cell: (po) => po.poNumber,
    },
    {
      key: 'vendor',
      header: 'Vendor',
      cell: (po) => po.vendor?.name ?? '—',
    },
    {
      key: 'type',
      header: 'Type',
      cell: (po) => PO_TYPE_LABELS[po.type] ?? po.type,
    },
    {
      key: 'status',
      header: 'Status',
      mobile: 'trailing',
      cell: (po) => <StatusBadge status={po.status} />,
    },
    {
      key: 'projectAmount',
      header: 'Project Amount',
      align: 'right',
      cell: (po) => lineAmount(po.projectAmount),
    },
  ];

  return (
    <div>
      {error && (
        <div className="tw-mb-3 tw-text-danger" role="alert">
          {error}
        </div>
      )}
      <DataTable
        columns={columns}
        data={orders}
        rowKey={(po) => po.id}
        loading={loading}
        framed
        hover
        onRowClick={(po) => navigate(`/purchase-orders/${po.id}`)}
        isExpanded={(po) => expanded.has(po.id)}
        renderExpanded={(po) => <ProjectLines lines={po.lines} />}
        emptyMessage="No purchase orders for this project."
      />
    </div>
  );
};

const ProjectLines: React.FC<{ lines: ProjectPurchaseOrderLine[] }> = ({ lines }) => {
  if (lines.length === 0) {
    return <div className="tw-p-3 tw-text-sm tw-text-content-muted">No line items for this project.</div>;
  }
  return (
    <div className="tw-overflow-x-auto tw-p-3">
      <table className="tw-w-full tw-text-sm">
        <thead>
          <tr className="tw-text-left tw-text-xs tw-tracking-wide tw-text-content-muted">
            <th className="tw-pb-1 tw-pr-4 tw-font-medium">Task</th>
            <th className="tw-pb-1 tw-pr-4 tw-font-medium">Description</th>
            <th className="tw-pb-1 tw-pr-4 tw-font-medium tw-text-right">Quantity</th>
            <th className="tw-pb-1 tw-pr-4 tw-font-medium">Unit</th>
            <th className="tw-pb-1 tw-font-medium tw-text-right">Amount</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((line) => (
            <tr key={line.id} className="tw-border-t tw-border-line">
              <td className="tw-py-1 tw-pr-4">{line.taskNumber ?? '—'}</td>
              <td className="tw-py-1 tw-pr-4">{line.description ?? '—'}</td>
              <td className="tw-py-1 tw-pr-4 tw-text-right tw-tabular-nums">{line.quantity}</td>
              <td className="tw-py-1 tw-pr-4">{line.unit ?? '—'}</td>
              <td className="tw-py-1 tw-text-right tw-tabular-nums">{lineAmount(line.amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ProjectPurchaseOrdersTab;
